import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { cn } from "../../components/lib/utils";
import {Badge} from "../../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { motion, useAnimation } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { datePickerStyles } from "../../components/constants/styles";
import "react-datepicker/dist/react-datepicker.css";
import { getPublicJobAppliedList, getSubJobAppliedList, updatePublicJobApplication, updateSubJobApplication } from "../../services/jobsapi";
import { useRbac } from "../../context";
import { getStatusBadgeColor } from "../../components/utils/application.utils";

interface StatusHistoryItem {
  previousStatus: string;
  newStatus: string;
  changedByUserId: string;
  changedAt: string;
  rejectionReason?: string;
}

interface Application {
  _id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  appliedAt: string;
  status: string;
  statusHistory?: StatusHistoryItem[];
  performedBy: string;
resumeUrl?: string;
  // Public candidate profile fields
  fatherName?: string;
  gender?: string;
  currentLocationDetails?: string;
  dateOfBirth?: string;
  aadharNumber?: string;
  highestQualification?: string;
  spokenLanguages?: string[];
  totalExperienceYears?: number;
  totalExperienceMonths?: number;
  skills?: string[];
  preferredWorkTypes?: string[];
}

interface PaginationState {
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export function PublicJobApplications() {
const navigate = useNavigate();
  const controls = useAnimation();
  // Inject custom styles for DatePicker
  useEffect(() => {
    
    if (typeof document !== "undefined" && datePickerStyles) {
      const styleSheet = document.createElement("style");
      styleSheet.innerText = datePickerStyles;
      document.head.appendChild(styleSheet);
      return () => {
        if (document.head.contains(styleSheet)) {
          document.head.removeChild(styleSheet);
        }
      };
    }
  }, []);

  // State management
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJob, setSelectedJob] = useState("all");
  const [selectedDateFrom, setSelectedDateFrom] = useState("");
  const [selectedDateTo, setSelectedDateTo] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [jobs, setJobs] = useState<any[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [loadingId, setLoadingId] = useState("");
  
  // Rejection modal state
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [rejectionError, setRejectionError] = useState("");
  
  // Popover state for rejection details
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  
  // Simple modal state for rejection details
  const [showRejectionDetailsModal, setShowRejectionDetailsModal] = useState(false);
  const [selectedRejectionApp, setSelectedRejectionApp] = useState<Application | null>(null);

  // Application details modal state
  const [showApplicationDetailsModal, setShowApplicationDetailsModal] = useState(false);
  const [selectedApplicationForDetails, setSelectedApplicationForDetails] = useState<Application | null>(null);

  const user = useRbac();

  // Status options: only ADMIN can see Shortlisted/Rejected filters; others only see Applied
  const jobOptions: SearchableSelectOption[] = useMemo(() => {
    const base = [{ value: "all", label: "Status" }, { value: "applied", label: "Applied" }];
    if (user?.role === "ADMIN") {
      return [...base, { value: "shortlisted", label: "Shortlisted" }, { value: "rejected", label: "Rejected" }];
    }
    return base;
  }, [user?.role, isLoadingJobs]);

  // Mock fetch jobs
  const fetchJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    try {
      // Mock job data
      const mockJobs = [
        { id: "1", title: "Software Engineer" },
        { id: "2", title: "Product Manager" },
        { id: "3", title: "Data Analyst" },
      ];
      setJobs(mockJobs);
    } catch (err: any) {
      console.error("Failed to fetch jobs:", err);
      setJobs([]);
      toast.error("Failed to load jobs for filtering");
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  // Fetch applications: non-ADMIN only ever gets "applied" status (rejected/shortlisted hidden)
  const fetchApplications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const effectiveStatus = user?.role === "ADMIN" ? selectedJob : "applied";
      const query = {
        search: searchTerm,
        selectedDateFrom,
        selectedDateTo,
        skip: (pagination.page - 1) * pagination.limit,
        limit: pagination.limit,
        status: effectiveStatus,
      };
      const jobs = await getPublicJobAppliedList(query);
      setApplications(jobs[0].data);
      setPagination((prev) => {
        const totalPages = Math.ceil(jobs[0]?.metadata[0]?.totalResults / pagination.limit);
        const totalResults = jobs[0]?.metadata[0]?.totalResults || 0;
        
        // If current page is greater than total pages, reset to page 1
        const currentPage = prev.page > totalPages ? 1 : prev.page;
        
        return {
          ...prev,
          page: currentPage,
          totalPages,
          totalResults,
        };
      });
    } catch (err: any) {
      setError(err.message || "Failed to fetch applications");
      toast.error(err.message || "Failed to fetch applications", { duration: 4000 });
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, selectedJob, selectedDateFrom, selectedDateTo, sortBy, sortOrder, user?.role]);

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [fetchApplications, fetchJobs]);

  // Non-ADMIN: ensure status filter is only "all" or "applied" (no shortlisted/rejected)
  useEffect(() => {
    if (user?.role !== "ADMIN" && (selectedJob === "shortlisted" || selectedJob === "rejected")) {
      setSelectedJob("applied");
      setPagination((p) => ({ ...p, page: 1 }));
    }
  }, [user?.role, selectedJob]);

  // Debounced search
  const debounce = useCallback((fn: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout | null = null;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fn(...args);
        timer = null;
      }, delay);
    };
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 500),
    [debounce]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      debouncedSearch(value);
      // fetchApplications() will be called automatically due to useEffect dependency on searchTerm
    },
    [debouncedSearch]
  );

  // Date range validation
  const isValidDateRange = useCallback(() => {
    if (!selectedDateFrom || !selectedDateTo) return true;
    return new Date(selectedDateFrom) <= new Date(selectedDateTo);
  }, [selectedDateFrom, selectedDateTo]);

  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (!isValidDateRange()) {
      setDateError("End date cannot be before start date.");
    } else {
      setDateError(null);
    }
  }, [selectedDateFrom, selectedDateTo, isValidDateRange]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page }));
      // fetchApplications() will be called automatically due to useEffect dependency on pagination.page
    }
  };

  // Toggle logic for Accept/Reject
const handleAccept = async (_id: string) => {
  try {
    setLoadingId(_id)
    await updatePublicJobApplication(_id, "shortlisted");
    

    setApplications((prev) =>
      prev.map((app) =>
        app._id === _id
          ? { ...app, status: "shortlisted" }
          : app
      )
    );
    setLoadingId("")
    toast.success(`Application ${_id} accepted!`);
  } catch (error: any) {
    // Check if it's an Axios error with a status code
    setLoadingId("")
    if (error.response?.status === 409) {
      toast.error("This application already exists or is duplicated.");
    } else {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  }
};


  const handleReject = (_id: string) => {
    setSelectedApplicationId(_id);
    setRejectionReason("");
    setRejectionError("");
    setShowRejectionModal(true);
  };

  const handleConfirmRejection = async () => {
    if (!rejectionReason.trim()) {
      setRejectionError("Rejection reason is required");
      return;
    }

    if (!selectedApplicationId) {
      toast.error("No application selected");
      return;
    }

    try {
      setLoadingId(selectedApplicationId);
      await updatePublicJobApplication(selectedApplicationId, 'rejected', rejectionReason);
      
      setApplications((prev) =>
        prev.map((app) =>
          app._id === selectedApplicationId
            ? {
              ...app,
              status: "rejected"
            }
            : app
        )
      );
      
      toast.success(`Application ${selectedApplicationId} rejected successfully`);
      setShowRejectionModal(false);
      setRejectionReason("");
      setSelectedApplicationId(null);
      setRejectionError("");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to reject application");
    } finally {
      setLoadingId("");
    }
  };

  const handleCancelRejection = () => {
    setShowRejectionModal(false);
    setRejectionReason("");
    setSelectedApplicationId(null);
    setRejectionError("");
  };

  const handleShowRejectionDetails = (app: Application) => {
    setSelectedRejectionApp(app);
    setShowRejectionDetailsModal(true);
  };

  const handleShowApplicationDetails = (app: Application) => {
    setSelectedApplicationForDetails(app);
    setShowApplicationDetailsModal(true);
  };

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className,
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors sticky top-0 bg-[#FFF5F2] z-10",
        className
      )}
      onClick={() => {
        if (sortBy === column) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(column);
          setSortOrder("asc");
        }
      }}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  // Non-ADMIN: only show "applied" applications (rejected/shortlisted hidden)
  const displayedApplications = useMemo(() => {
    if (user?.role === "ADMIN") return applications;
    return applications.filter((app) => app.status === "applied");
  }, [applications, user?.role]);

  // Helper function to get rejection reason - Updated for click-based popover
  const getRejectionReason = (application: Application): string | null => {
    console.log('Checking rejection reason for application:', application._id, 'Status:', application.status);
    if (application.status !== 'rejected') return null;
    
    // Safety check for statusHistory
    if (!application.statusHistory || !Array.isArray(application.statusHistory)) {
      console.log('No statusHistory for application:', application._id);
      return null;
    }
        
    // Find the most recent rejection in status history
    const rejectionHistory = application.statusHistory
      .filter(history => history.newStatus === 'rejected')
      .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
    
    const reason = rejectionHistory.length > 0 ? rejectionHistory[rejectionHistory.length-1].rejectionReason || null : null;
    console.log('Rejection reason for', application._id, ':', reason);
    return reason;
  };
  

  return (
    <div>
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4 flex">
        <div>
          <Button variant="ghost" size="sm" className="p-1 sm:p-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <div className="w-full">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Job Applications</h1>
                <p className="text-xs sm:text-sm text-gray-500">Manage and track public job applications efficiently</p>
              </div>
            </div>
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search Applications..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 w-full sm:w-64 border-gray-300"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <SearchableSelect
              options={jobOptions}
              value={selectedJob}
              onValueChange={(v) => {
                setSelectedJob(v);
                setPagination((p) => ({ ...p, page: 1 }));
                // fetchApplications() will be called automatically due to useEffect dependency on selectedJob
              }}
              placeholder={isLoadingJobs ? "Loading jobs..." : "All Jobs"}
              emptyText="No jobs found"
              searchPlaceholder="Search jobs..."
              className="w-37 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              searchable
            />
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={selectedDateFrom}
                onChange={(e) => {
                  setSelectedDateFrom(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                  // fetchApplications() will be called automatically due to useEffect dependency on selectedDateFrom
                }}
                max={selectedDateTo || undefined}
                className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                title="From Date"
              />
              <Input
                type="date"
                value={selectedDateTo}
                onChange={(e) => {
                  setSelectedDateTo(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                min={selectedDateFrom || undefined}
                className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                title="To Date"
              />
            </div>
          </div>
          {dateError && <p className="text-red-500 text-sm mt-2">{dateError}</p>}
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-background relative">
        <div className="h-[calc(100vh-275px)] overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: "#FFF5F2" }}>
              <tr>
                <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
                <SortableHeader column="candidate.name" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-gray-200">
                  Name
                </SortableHeader>
                <SortableHeader column="companyName" className="bg-[#FFF5F2] min-w-48">Company Name</SortableHeader>
                <SortableHeader column="jobTitle" className="bg-[#FFF5F2] min-w-48">Job Title</SortableHeader>
                <SortableHeader column="candidate.phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
                <SortableHeader column="candidate.email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
                <SortableHeader column="appliedAt" className="bg-[#FFF5F2] min-w-40">Applied At</SortableHeader>
                {user.role === "ADMIN" && <SortableHeader column="postedByUser" className="bg-[#FFF5F2] min-w-40">performed by</SortableHeader>}  
                {user.role !== "INVESTOR" && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Actions
                </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <svg
                        className="animate-spin h-5 w-5 text-orange-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-gray-500">{error}</p>
                  </td>
                </tr>
              ) : displayedApplications.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-gray-500">No applications found matching your criteria.</p>
                  </td>
                </tr>
              ) : (
                displayedApplications.map((app, index) => (
                  <tr
                    key={app._id}
                    className={cn("hover:bg-gray-50 transition-colors cursor-pointer")}
                    onClick={() => handleShowApplicationDetails(app)}
                  >
                    <td className="px-4 py-4"></td>
                    <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-gray-200">
                      <span className="font-medium text-gray-900">{app.name}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">{app.company}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{app.jobTitle}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy phone number"
                        onClick={(e) => {
                           if(! (user.role === "COMPANY" && index > 4) ){
                          e.stopPropagation();
                          navigator.clipboard.writeText(app.phone);
                          toast.success("Phone number copied!");
                        }}
                      }
                      >
                        {user.role === "COMPANY" && index > 4 
                          ? `${app.phone.slice(0, 2)}xxxxxx${app.phone.slice(-2)}` 
                          : app.phone}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy email"
                        onClick={(e) => {
                          if(! (user.role === "COMPANY" && index > 4) ){
                            e.stopPropagation();
                            navigator.clipboard.writeText(app.email);
                            toast.success("Email copied!");
                          }
                        }}
                      >
                        {user.role === "COMPANY" && index > 4 
                        ? `${app.email.slice(0, 4)}xxxxxx${app.email.slice(-4)}` 
                        : app.email}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(app.appliedAt).toLocaleDateString()}
                    </td>
                    {user.role === "ADMIN" && <td className="px-4 py-4 text-sm text-gray-500">
                      {app.performedBy}
                    </td>}

                    <td className="px-4 py-4">
                      {user.role !== "INVESTOR" && (
                      <div className="flex gap-2">
                        {app.status === "applied" ? user.role ==="ADMIN" ? <Badge className={cn(getStatusBadgeColor("applied"),"pointer-events-none",)}>applied</Badge>  : <><Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "rounded-md border border-orange-300 px-3 py-1 text-sm font-medium text-orange-500 bg-white hover:bg-orange-50"
                        )}
                        disabled={loadingId == app._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAccept(app._id);
                        }}
                      >
                         Shortlist
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "rounded-md border border-orange-300 px-3 py-1 text-sm font-medium text-orange-500 bg-white hover:bg-orange-50"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(app._id);
                        }}
                      >
                        Reject
                      </Button></> : <>{app.status === "shortlisted" ? <Badge className={cn(getStatusBadgeColor("shortlisted"),"pointer-events-none",)}>Shortlisted</Badge> : 
                        getRejectionReason(app) ? (
                          <Badge 
                            className={getStatusBadgeColor("rejected")}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Rejected badge clicked for:', app._id);
                              handleShowRejectionDetails(app);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            Rejected
                          </Badge>
                        ) : (
                          <Badge 
                            className={getStatusBadgeColor("rejected")}
                            title={getRejectionReason(app) ? `Rejection Reason: ${getRejectionReason(app)}` : undefined}
                          >
                            Rejected
                          </Badge>
                        )
                      }</>}
                     
                    </div>
                    )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-2 p-4 pl-8">
          <div className="text-sm text-gray-500">
            Showing {displayedApplications.length} of {pagination.totalResults} applications
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {pagination.totalPages > 0 && Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter(
                (pageNum) =>
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
              )
              .map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pagination.page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  disabled={isLoading || pagination.page == pageNum}
                  className={
                    pagination.page === pageNum
                      ? "bg-orange-600 text-white hover:bg-orange-700"
                      : "border-gray-200 hover:bg-orange-50"
                  }
                >
                  {pageNum}
                </Button>
              ))}
            {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
              <span className="text-sm text-gray-500">...</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Rejection Reason Modal */}
      <Dialog open={showRejectionModal} onOpenChange={setShowRejectionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Reject Application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason" className="text-sm font-medium text-gray-700">
                Rejection Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="rejection-reason"
                placeholder="Please provide a reason for rejecting this application..."
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  setRejectionError("");
                }}
                className={`min-h-[100px] resize-none ${
                  rejectionError ? "border-red-500 focus:border-red-500" : ""
                }`}
              />
              {rejectionError && (
                <p className="text-sm text-red-500">{rejectionError}</p>
              )}
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancelRejection}
              disabled={loadingId === selectedApplicationId}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRejection}
              disabled={loadingId === selectedApplicationId || !rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {loadingId === selectedApplicationId ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Rejecting...
                </>
              ) : (
                "Reject Application"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Details Modal */}
      <Dialog open={showRejectionDetailsModal} onOpenChange={setShowRejectionDetailsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Rejection Details
            </DialogTitle>
          </DialogHeader>
          {selectedRejectionApp && (
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">{selectedRejectionApp.name}</h4>
                  <Badge variant="secondary" className="text-xs">Rejected</Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Job:</span>
                    <span className="font-medium">{selectedRejectionApp.jobTitle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Company:</span>
                    <span className="font-medium">{selectedRejectionApp.company}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Applied:</span>
                    <span className="font-medium">{new Date(selectedRejectionApp.appliedAt).toLocaleDateString()}</span>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Rejection Reason:</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{getRejectionReason(selectedRejectionApp)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectionDetailsModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Details Modal */}
      <Dialog
        open={showApplicationDetailsModal}
        onOpenChange={(open) => {
          setShowApplicationDetailsModal(open);
          if (!open) setSelectedApplicationForDetails(null);
        }}
      >
        <DialogContent className="w-[95vw] max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl no-scrollbar">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="h-10 w-10 sm:h-12 sm:w-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                {(selectedApplicationForDetails?.name || "A").charAt(0).toUpperCase()}
              </div>
              <span className="break-words">{selectedApplicationForDetails?.name}</span>
            </DialogTitle>
          </DialogHeader>

          {selectedApplicationForDetails && (
            <div className="space-y-4 sm:space-y-6 p-0">
              {/* Basic Information */}
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Email</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base break-words">
                        {selectedApplicationForDetails.email || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Phone</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.phone || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Father's Name</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.fatherName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Gender</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.gender || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Location</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base break-words">
                        {selectedApplicationForDetails.currentLocationDetails || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Date of Birth</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.dateOfBirth
                          ? new Date(selectedApplicationForDetails.dateOfBirth).toLocaleDateString("en-GB")
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Aadhar Number</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.aadharNumber || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Highest Qualification</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.highestQualification || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Spoken Languages</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {Array.isArray(selectedApplicationForDetails.spokenLanguages) &&
                        selectedApplicationForDetails.spokenLanguages.length > 0
                          ? selectedApplicationForDetails.spokenLanguages.join(", ")
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Experience</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.totalExperienceYears !== undefined &&
                        selectedApplicationForDetails.totalExperienceMonths !== undefined
                          ? `${selectedApplicationForDetails.totalExperienceYears} years ${selectedApplicationForDetails.totalExperienceMonths} months`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Skills</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Array.isArray(selectedApplicationForDetails.skills) &&
                        selectedApplicationForDetails.skills.length > 0 ? (
                          selectedApplicationForDetails.skills.slice(0, 6).map((skill, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))
                        ) : (
                          <span className="font-medium text-gray-900 text-sm sm:text-base">N/A</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Work Mode</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">
                      {Array.isArray(selectedApplicationForDetails.preferredWorkTypes) &&
                      selectedApplicationForDetails.preferredWorkTypes.length > 0
                        ? selectedApplicationForDetails.preferredWorkTypes.join(", ")
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Job Category</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">N/A</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Employment Type</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">N/A</p>
                  </div>


                  <div>
                    {selectedApplicationForDetails.resumeUrl && (
                      <>
                  <p className="text-xs sm:text-sm text-gray-600">Resume</p>
                   <a href={selectedApplicationForDetails.resumeUrl} target="_blank" rel="noopener noreferrer"> <p className="text-xs sm:text-base  text-blue-500 cursor-pointer font-medium">View Resume</p></a>
                   </>
                    )}
                  </div>
                </div>
              </div>

              {/* Application Status */}
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Application Status
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Current Status</p>
                    <Badge className={cn(getStatusBadgeColor(selectedApplicationForDetails.status), "pointer-events-none")}>
                      {selectedApplicationForDetails.status || "N/A"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Expected Joining Date</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">N/A</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Applied At</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">
                      {selectedApplicationForDetails.appliedAt
                        ? new Date(selectedApplicationForDetails.appliedAt).toLocaleDateString("en-GB")
                        : "N/A"}
                    </p>
                  </div>
                </div>

                {selectedApplicationForDetails.status === "rejected" &&
                  getRejectionReason(selectedApplicationForDetails) && (
                    <div className="mt-4 bg-red-50 border border-red-100 rounded-lg p-3">
                      <p className="text-xs text-red-700 font-medium">Rejection Reason</p>
                      <p className="text-sm text-gray-900 mt-1">
                        {getRejectionReason(selectedApplicationForDetails)}
                      </p>
                    </div>
                  )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplicationDetailsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div> 
    )
}

export default function ViewApplications({ onAddCandidate }: { onAddCandidate?: () => void }) {
  return <PublicJobApplications />;
}